import {Button, Flex, HStack, Stack, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import useOcapiSite from '../../../commerce-api/hooks/useOcapiSite'
import HybridLink from '../../../components/hybrid-link'
import {
    Box2OutlineIcon,
    DeliveryFastOutlineIcon,
    VerifiedIcon,
    SaveExtraIcon
} from '../../../components/icons'
import {ContentAssetModal, useContentAssetModal} from '../../../hooks/use-content-asset-modal'
import {customerServiceUrlBuilder} from '../../../utils/url'
import ProductAvailability from './product-availability'
import DisplayPrice from '../../../components/display-price'
import AddToRegistryCTA from '../../../components/add-to-registry-cta'
import AddToCartCTA, {Skeleton as AddToCartCTASkeleton} from '../../../components/add-to-cart-cta'
import {HYBRID_CONTENT, HYBRID_STORE_LOCATOR, STORE_LOCATOR_HREF} from '../../../constants'
import WhereToBuyCTA from '../../../components/where-to-buy-cta'
import useMultiSite from '../../../hooks/use-multi-site'

const CockpitOrderSection = ({
    isProductASet,
    isPromotionsLoading,
    isVariantLoading,
    product,
    promotions,
    cro039VariantActive,
    ...props
}) => {
    const {site} = useMultiSite()
    const {shippingContentAssetId} = useOcapiSite()
    const contentModal = useContentAssetModal(shippingContentAssetId)
    const {formatMessage} = useIntl()

    return (
        <Stack spacing={4}>
            {!site?.isContentOnlySite && !isProductASet && (
                <Stack spacing={0}>
                    <Flex gap={4.5} alignItems="flex-end" justifyContent="space-between">
                        <ProductAvailability
                            loading={isVariantLoading}
                            flexShrink="0"
                            product={product}
                        />
                        <DisplayPrice
                            size="xl"
                            product={product}
                            loading={isPromotionsLoading || isVariantLoading}
                            textStyle="Regular XLarge"
                        />
                    </Flex>
                    <Text textStyle="Regular Small" textAlign="right">
                        <FormattedMessage
                            id="global.taxes_included"
                            defaultMessage="tax included"
                        />
                    </Text>
                </Stack>
            )}
            <Stack spacing={3}>
                {!isVariantLoading ? (
                    <AddToCartCTA
                        isPromotionsLoading={isPromotionsLoading}
                        isVariantLoading={isVariantLoading}
                        product={product}
                        promotions={promotions}
                        {...props}
                    />
                ) : (
                    <AddToCartCTASkeleton />
                )}
                {!cro039VariantActive && (
                    <>
                        {!site?.isContentOnlySite && <WhereToBuyCTA product={product} />}
                        <Button
                            variant="outline"
                            size="lg"
                            width="100%"
                            as={HybridLink}
                            page={HYBRID_STORE_LOCATOR}
                            to={STORE_LOCATOR_HREF}
                        >
                            <FormattedMessage
                                id="product_view.button.experience_in_store"
                                defaultMessage="Experience in store"
                            />
                        </Button>
                    </>
                )}
            </Stack>
            <Stack spacing={cro039VariantActive ? 4 : 2} pt={cro039VariantActive && 2}>
                {product?.c_estimatedDelivery && (
                    <HStack spacing={2} alignItems="center">
                        <Box2OutlineIcon boxSize={4} />
                        <Text textStyle="Regular Small">
                            {formatMessage(
                                {
                                    id: 'checkout.shipping.delivery_time',
                                    defaultMessage: 'Est. delivery {time}',
                                    description:
                                        'The date range when delivery is expected to happen. {time} contains range like "24.11.2021 - 26.11.2021"'
                                },
                                {time: product.c_estimatedDelivery}
                            )}
                        </Text>
                    </HStack>
                )}
                {cro039VariantActive ? (
                    <>
                        <HStack spacing={2} alignItems="center">
                            <DeliveryFastOutlineIcon boxSize={4} />
                            <Text textStyle="Regular Small">
                                Kostenloser Versand für Bestellungen über 99 €
                            </Text>
                        </HStack>
                        <HStack spacing={2} alignItems="center">
                            <SaveExtraIcon boxSize={4} />
                            <Text textStyle="Regular Small">
                                30 Tage kostenlose Retoure (personalisierte Produkte ausgenommen)
                            </Text>
                        </HStack>
                        <HStack spacing={2} alignItems="center">
                            <VerifiedIcon boxSize={4} />
                            <Text textStyle="Regular Small">7 Jahre Stokke Garantie</Text>
                        </HStack>
                    </>
                ) : (
                    <Stack spacing={4} paddingTop={2} alignItems="flex-start">
                        <AddToRegistryCTA product={product} />
                        {!site?.isContentOnlySite && (
                            <Button
                                variant="link"
                                width="max-content"
                                onClick={contentModal.onOpen}
                            >
                                <HStack spacing={2} align="center">
                                    <DeliveryFastOutlineIcon boxSize={4} />
                                    <Text textStyle="Regular Small">
                                        <FormattedMessage
                                            id="product_detail.link.shipping_and_returns"
                                            defaultMessage="Shipping and Returns"
                                        />
                                    </Text>
                                </HStack>
                            </Button>
                        )}
                        {product?.c_pdpDescriptionContent ? (
                            <HybridLink
                                maxW="full"
                                page={HYBRID_CONTENT}
                                to={customerServiceUrlBuilder(product.c_pdpDescriptionContent)}
                            >
                                <Text textStyle="Regular Small">
                                    <FormattedMessage
                                        id="product_detail.link.siibs"
                                        defaultMessage="Important Ergonomic and Usage Information"
                                    />
                                </Text>
                            </HybridLink>
                        ) : null}
                    </Stack>
                )}
            </Stack>
            <ContentAssetModal {...contentModal} variant="pdp" />
        </Stack>
    )
}

CockpitOrderSection.propTypes = {
    product: PropTypes.object,
    promotions: PropTypes.array,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    isProductASet: PropTypes.bool,
    cro039VariantActive: PropTypes.bool
}

export default CockpitOrderSection
